import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    MenuItem,
} from '@mui/material';
import apiClient from '../../services/apiClient';
import { J } from '@fullcalendar/core/internal-common';

interface AddLocationFormProps {
    userId: number;
    onSuccess: () => void;
    location?: any;
}

const locationTypes = ['Home', 'Work', 'Others'];

const AddLocationForm: React.FC<AddLocationFormProps> = ({ userId, onSuccess, location }) => {
    console.log('Editing location:', JSON.stringify(location, null, 2));
    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

    const normalizedType = location?.additional_info?.type
        ? ['Home', 'Work'].includes(capitalize(location.additional_info.type))
            ? capitalize(location.additional_info.type)
            : 'Others'
        : '';

    const [type, setType] = useState(normalizedType);

    const [customType, setCustomType] = useState(
        location && !['home', 'work'].includes(location?.additional_info?.type) ? location.additional_info.type : ''
    );
    const [door, setDoor] = useState(location?.additional_info?.address_details?.door_no || '');
    const [street, setStreet] = useState(location?.additional_info?.address_details?.road || '');
    const [area, setArea] = useState(location?.additional_info?.address_details?.suburb || '');
    const [city, setCity] = useState(location?.additional_info?.address_details?.city || '');
    const [state, setState] = useState(location?.additional_info?.address_details?.state || '');
    const [zip, setZip] = useState(location?.additional_info?.address_details?.postcode || '');
    const [country, setCountry] = useState(location?.additional_info?.address_details?.country || '');
    const [landmark, setLandmark] = useState(location?.additional_info?.address_details?.landmark || '');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!type || (type === 'Others' && !customType) || !door || !street || !area || !city || !state || !zip || !country) {
            setError('Please fill all required fields.');
            return;
        }

        setLoading(true);
        const finalType = type === 'Others' ? customType : type.toLowerCase();

        const payload = {
            user_id: userId,
            type: finalType,
            latitude: 0,
            longitude: 0,
            is_saved: true,
            is_confirmed: location?.is_confirmed || 0,
            additional_info: {
                timestamp: new Date().toISOString().slice(0, 19).replace('T', ' '),
                display_address: `${door}, ${street}, ${area}, ${city}, ${state}, ${zip}, ${country}`,
                type: finalType,
                address_details: {
                    door_no: door,
                    road: street,
                    suburb: area,
                    city,
                    state,
                    postcode: zip,
                    country,
                    landmark: landmark || '',
                },
            },
        };

        try {
            let response;
            if (location?.id) {
                response = await apiClient(`/v1/resource/UserLocation/${location.id}`, {
                    method: 'PATCH',
                    body: payload,
                });
            } else {
                response = await apiClient('/v1/resource/UserLocation', {
                    method: 'POST',
                    body: payload,
                });
            }

            console.log(location?.id ? 'Location updated:' : 'Location added:', response);

            onSuccess();
        } catch (err: any) {
            console.error('Location error:', err);
            setError(err.message || 'Failed to save location. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fieldStyle = {
        background: 'rgba(255,255,255,0.7)',
        borderRadius: 2,
        flex: 1,
        minWidth: 'calc(50% - 8px)'
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                p: 3,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {location?.id ? 'Edit Location' : 'Add New Location'}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField
                    select
                    label="Location Type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    required
                    sx={fieldStyle}
                >
                    {locationTypes.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>

                {type === 'Others' && (
                    <TextField
                        label="Specify Type"
                        value={customType}
                        onChange={(e) => setCustomType(e.target.value)}
                        required
                        sx={fieldStyle}
                    />
                )}

                <TextField label="Door / Flat No" value={door} onChange={(e) => setDoor(e.target.value)} required sx={fieldStyle} />
                <TextField label="Street / Lane" value={street} onChange={(e) => setStreet(e.target.value)} required sx={fieldStyle} />

                <TextField label="Area / Locality" value={area} onChange={(e) => setArea(e.target.value)} required sx={fieldStyle} />
                <TextField label="City / Town" value={city} onChange={(e) => setCity(e.target.value)} required sx={fieldStyle} />

                <TextField label="State / Province" value={state} onChange={(e) => setState(e.target.value)} required sx={fieldStyle} />
                <TextField label="Postal Code / ZIP" value={zip} onChange={(e) => setZip(e.target.value)} required sx={fieldStyle} />

                <TextField label="Country" value={country} onChange={(e) => setCountry(e.target.value)} required sx={fieldStyle} />
                <TextField label="Landmark (Optional)" value={landmark} onChange={(e) => setLandmark(e.target.value)} sx={fieldStyle} />
            </Box>

            <Button type="submit" variant="contained" color="primary" disabled={loading} sx={{ mt: 3, py: 1.5, fontWeight: 600, borderRadius: 2, width: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                {loading ? (location?.id ? 'Updating...' : 'Adding...') : (location?.id ? 'Update Location' : 'Add Location')}
            </Button>
        </Box>
    );
};

export default AddLocationForm;
